/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : dynamicFormOld.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import UpdateIcon from "@mui/icons-material/Update";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import {
  CustomLogsDialog,
  DynamicFormTemplate,
} from "../../../components";
import customConsole from "../../../config/customConsole";
import generatePDF from "react-to-pdf";
import typeApprovalAPI from "../../../redux/actions/typeApprovalAPI";
import { HandleApiActions } from "../../../redux/actions";
import { useDispatch } from "react-redux";
import { FORM_TYPES } from "../../../config/appConstants";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import AddButtonV2 from "../../../components/customComponentsV2/common/button/AddButtonV2";
import CustomFormTextFieldV2 from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import AttributeUpdateDialog from "../../labcar/Attributes/AttributeUpdateDialog";

const ALIGN_TYPES = { LEFT: "left", CENTER: "center", RIGHT: "right" };

const getFormTypeName = (type) => {
  if (type === FORM_TYPES?.EE) return "EE Component";
  if (type === FORM_TYPES?.SWM) return "Software Module";
  if (type === FORM_TYPES?.VEHICLE) return "Vehicle Component";
  return "-";
};

const DynamicFormOld = ({
  open,
  onClose,
  formType,
  projectId,
  variantId,
  ticketId,
  intrId,
  Title,
  dialogContent,
  onConfirm,
  onDisabled,
  dynamicFormData,
  setDynamicFormData,
  selectedComponent,
  componentType,
}) => {
  const dispatch = useDispatch();

  const templateRef = useRef();

  const [selectedHeaders, setSelectedHeaders] = useState([ALIGN_TYPES?.CENTER]);
  const [selectedFooters, setSelectedFooters] = useState([ALIGN_TYPES?.CENTER]);
  const [selectedSignatures, setSelectedSignatures] = useState([
    ALIGN_TYPES?.RIGHT,
  ]);

  const [openTemplateChangeLogsDialog, setOpenTemplateChangeLogsDialog] =
    useState(false);
  const [templateChangeLogsList, setTemplateChangeLogsList] = useState([]);
  const [templateLogsDialogData, setTemplateLogsDialogData] = useState({});
  const [openAttributeUpdateDialog, setOpenAttributeUpdateDialog] =
    useState(false);

  const [updatedFieldsValues, setUpdatedFieldsValues] = useState([]);
  const [currentFieldValue, setCurrentFieldValue] = useState({
    field_name: "",
    value: "",
    default_value: "",
    edit_type: "",
    sequence: "",
    reason: "",
  });
  const [showUpdateFieldValues, setShowUpdateFieldValues] = useState(false);

  const headerOptionsList = [
    {
      label: "Left",
      position: ALIGN_TYPES?.LEFT,
      value: "",
    },
    {
      label: "Center",
      position: ALIGN_TYPES?.CENTER,
      value: "",
    },
    {
      label: "Right",
      position: ALIGN_TYPES?.RIGHT,
      value: "",
    },
  ];

  const footerOptionsList = [
    {
      label: "Left",
      position: ALIGN_TYPES?.LEFT,
      value: "",
    },
    {
      label: "Center",
      position: ALIGN_TYPES?.CENTER,
      value: "",
    },
    {
      label: "Right",
      position: ALIGN_TYPES?.RIGHT,
      value: "",
    },
  ];

  const signatureOptionsList = [
    {
      label: "Left",
      position: ALIGN_TYPES?.LEFT,
      value: "",
    },
    {
      label: "Right",
      position: ALIGN_TYPES?.RIGHT,
      value: "",
    },
  ];

  const handleGetTemplateChangeLogs = ({ pageNo, pageSize }) => {
    let apiDetails = typeApprovalAPI.get_dynamic_form_logs;

    if (dynamicFormData?.form_id) {
      customConsole({
        ...apiDetails,
        params: {
          form_id: Number(dynamicFormData?.form_id),
          page_number: pageNo,
          page_size: pageSize,
        },
        show_toast: false,
      });

      dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            form_id: Number(dynamicFormData?.form_id),
            page_number: pageNo,
            page_size: pageSize,
          },
          show_toast: false,
        }),
      ).then((resp) => {
        if (resp?.error) {
          setTemplateChangeLogsList([]);
        } else {
          setTemplateChangeLogsList(resp?.data || []);
        }
      });
    }
  };

  const handleTemplateLogsDialogOpen = (data) => {
    setOpenTemplateChangeLogsDialog(true);

    setTemplateLogsDialogData(data);
  };
  const handleTemplateLogsDialogClose = () => {
    setTemplateLogsDialogData(null);
    setTemplateChangeLogsList([]);

    setOpenTemplateChangeLogsDialog(false);
  };

  const getSelectedComponentType = () => {
    if (componentType) return String(componentType).toLowerCase();
    if (selectedComponent?.swm_id) return "swm";
    if (selectedComponent?.ee_id) return "ee";
    if (selectedComponent?.vc_id) return "vc";
    if (selectedComponent?.vs_id) return "vs";

    return "vc";
  };

  const updateFieldValues = () => {
    let apiDetails = typeApprovalAPI.update_dynamic_form;

    if (dynamicFormData?.form_id) {
      customConsole({
        ...apiDetails,
        params: {
          form_id: Number(dynamicFormData?.form_id),
          field_details: updatedFieldsValues,
        },
        show_toast: true,
      });

      dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            form_id: Number(dynamicFormData?.form_id),
            field_details: updatedFieldsValues,
            template_tag: "OLD",
          },
          show_toast: true,
        }),
      );
    }

    setUpdatedFieldsValues([]);
  };

  const handleUpdateFieldValues = () => {
    if (currentFieldValue?.sequence) {
      setUpdatedFieldsValues((prev) => [
        ...prev,
        {
          field_name: currentFieldValue?.field_name,
          value: currentFieldValue?.value,
          reason: currentFieldValue?.reason,
        },
      ]);

      setDynamicFormData((prev) => ({
        ...prev,
        fields: prev?.fields?.map((field1) =>
          field1?.sequence === currentFieldValue?.sequence
            ? { ...field1, value: currentFieldValue?.value }
            : field1,
        ),
      }));
    }

    resetFieldValuesInputs();
  };

  const resetFieldValuesInputs = () => {
    setCurrentFieldValue({
      field_name: "",
      value: "",
      default_value: "",
      edit_type: "",
      sequence: "",
      reason: "",
    });

    setShowUpdateFieldValues(false);
  };

  const handleFieldValueChange = (field, value) => {
    setCurrentFieldValue((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditUpdateField = (fieldName) => {
    let fieldToUpdate = dynamicFormData?.fields?.find(
      (field1) => field1?.field_name === fieldName,
    );

    if (fieldToUpdate) {
      setCurrentFieldValue(fieldToUpdate);
      setShowUpdateFieldValues(true);
    }
  };

  useEffect(() => {
    const validPositions = ["left", "center", "right"];
    const validPositions1 = ["left", "right"];

    let selectedHeadersList =
      dynamicFormData?.headers
        ?.map((el) =>
          validPositions?.includes(el?.position) ? String(el?.position) : null,
        )
        ?.filter(Boolean) || [];
    setSelectedHeaders(selectedHeadersList);

    let selectedSignaturesList =
      dynamicFormData?.signatures
        ?.map((el) =>
          validPositions1?.includes(el?.position) ? String(el?.position) : null,
        )
        ?.filter(Boolean) || [];
    setSelectedSignatures(selectedSignaturesList);

    let selectedFootersList =
      dynamicFormData?.footers
        ?.map((el) =>
          validPositions?.includes(el?.position) ? String(el?.position) : null,
        )
        ?.filter(Boolean) || [];
    setSelectedFooters(selectedFootersList);
  }, [dynamicFormData]);

  useEffect(() => {
    customConsole({ dynamicFormData17: dynamicFormData });
  }, [dynamicFormData]);

  return (
    <>
      <Box>
        {dynamicFormData?.intr_id ? (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                mb: 1,
                gap: 2,
              }}
            >
              <CustomButtonV2
                size={"small"}
                variant={"contained"}
                btnName={"Update"}
                startIcon={<UpdateIcon />}
                disabled={
                  !updatedFieldsValues?.length ||
                  !updatedFieldsValues?.every(
                    (item) => item?.field_name && item?.value && item?.reason,
                  )
                }
                handleOnClick={() => {
                  resetFieldValuesInputs();
                  updateFieldValues();
                }}
                btnStyle={{}}
              />
              <CustomButtonV2
                type="other"
                size="small"
                btnName="Log"
                handleOnClick={() =>
                  handleTemplateLogsDialogOpen({ pageNo: 1, pageSize: 25 })
                }
              />
              <AddButtonV2
                icon="download"
                handleOnClick={() =>
                  generatePDF(templateRef, { filename: "page.pdf" })
                }
              />

              <IconButton
                onClick={() => {
                  if (selectedComponent) {
                    setOpenAttributeUpdateDialog(true);
                  }
                }}
                disabled={!selectedComponent}
                aria-label="copy attributes"
              >
                <VisibilityOutlinedIcon />
              </IconButton>
            </Box>
            <Paper elevation={2} sx={styles.intDetailsContainer}>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  textAlign: "center",
                  pt: 0.4,
                }}
              >
                Form Details
              </Typography>
              <Divider sx={{ mt: 0.4, mb: 1 }} />
              <Grid container sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={styles.intDetailsTxt}>
                      <span style={styles.txtBold}>Intersection id: </span>
                      {dynamicFormData?.intr_id || "-"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={styles.intDetailsTxt}>
                      <span style={styles.txtBold}>Ticket id: </span>
                      {ticketId || "-"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={styles.intDetailsTxt}>
                      <span style={styles.txtBold}>Form name: </span>
                      {dynamicFormData?.name || "-"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={styles.intDetailsTxt}>
                      <span style={styles.txtBold}>Form type: </span>
                      {getFormTypeName(dynamicFormData?.type)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
            <Box sx={{ display: "flex", gap: 2.5 }}>
              <Box
                ref={templateRef}
                sx={{
                  minHeight: "315mm",
                  padding: "10px",
                  display: "flex",
                  textDecoration: "none",
                  justifyContent: "space-between",
                }}
              >
                <DynamicFormTemplate
                  showOnlyTemplate={true}
                  handleEditField={() => { }}
                  showFieldsUpdate={true}
                  handleEditUpdateField={handleEditUpdateField}
                  headerOptionsList={headerOptionsList}
                  signatureOptionsList={signatureOptionsList}
                  footerOptionsList={footerOptionsList}
                  selectedHeaders={selectedHeaders}
                  selectedSignatures={selectedSignatures}
                  selectedFooters={selectedFooters}
                  dynamicFormData={dynamicFormData}
                  setDynamicFormData={setDynamicFormData}
                />
              </Box>
              {showUpdateFieldValues ? (
                <Paper
                  elevation={2}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    px: 1,
                    py: 1.4,
                    m: 1,
                    border: "1px solid grey",
                    borderRadius: 1,
                    height: "280px",
                    width: "280px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "17px",
                      fontWeight: 600,
                      textAlign: "center",
                    }}
                  >
                    Update Field
                  </Typography>
                  <Divider
                    sx={{ m: -1, p: 0, border: "1px solid lightgrey" }}
                  />

                  <Box>
                    <Typography
                      variant="subtitle1"
                      color={"grey"}
                      component="label"
                      htmlFor="field-name"
                    >
                      field name*
                    </Typography>
                    <CustomFormTextFieldV2
                      size="small"
                      type="text"
                      disabled
                      value={currentFieldValue?.field_name || ""}
                      setValue={() => { }} // no-op (disabled field)
                      txtFldStyle={{
                        width: "220px",
                        mt: -0.7,
                      }}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="subtitle1"
                      color={"grey"}
                      component="label"
                      htmlFor="field-value"
                    >
                      Enter field value*
                    </Typography>
                    <CustomFormTextFieldV2
                      size="small"
                      type="text"
                      value={currentFieldValue?.value || ""}
                      setValue={(val) => handleFieldValueChange("value", val)}
                      txtFldStyle={{
                        width: "220px",
                        mt: -0.7,
                      }}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="subtitle1"
                      color={"grey"}
                      component="label"
                      htmlFor="reason"
                    >
                      Enter reason*
                    </Typography>
                    <CustomFormTextFieldV2
                      size="small"
                      type="text"
                      value={currentFieldValue?.reason || ""}
                      setValue={(val) => handleFieldValueChange("reason", val)}
                      txtFldStyle={{
                        width: "220px",
                        mt: -0.7,
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 1.4,
                      mt: 2,
                    }}
                  >
                    <CustomButtonV2
                      type="Close"
                      size="small"
                      btnName="Close"
                      handleOnClick={resetFieldValuesInputs}
                    />
                    <CustomButtonV2
                      type="submit"
                      size="small"
                      btnName="Update"
                      disabled={
                        !(
                          currentFieldValue?.field_name?.length &&
                          currentFieldValue?.value?.length &&
                          currentFieldValue?.reason?.length
                        )
                      }
                      startIcon={<UpdateIcon />}
                      handleOnClick={handleUpdateFieldValues}
                      btnStyle={{}}
                    />
                  </Box>
                </Paper>
              ) : null}
            </Box>
            <CustomLogsDialog
              open={openTemplateChangeLogsDialog}
              handleClose={handleTemplateLogsDialogClose}
              dialogTitle={"Change Logs"}
              dialogContent={templateChangeLogsList}
              data={templateLogsDialogData}
              getLogsData={handleGetTemplateChangeLogs}
            />
          </>
        ) : (
          <Box
            style={{
              height: "370px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">No attachments found</Typography>
          </Box>
        )}
      </Box>
      <AttributeUpdateDialog
        openAttributeUpdateDialog={openAttributeUpdateDialog}
        setOpenAttributeUpdateDialog={setOpenAttributeUpdateDialog}
        handleCloseAttributeUpdateDialog={() => {
          setOpenAttributeUpdateDialog(false);
        }}
        onClickSuccess={() => {
          setOpenAttributeUpdateDialog(false);
        }}
        selectedComponent={selectedComponent}
        componentType={getSelectedComponentType()}
        type="COPY"
        hideDefaultActions
      />
    </>
  );
};

DynamicFormOld.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  formType: PropTypes.string,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  variantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ticketId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  intrId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  Title: PropTypes.string,
  dialogContent: PropTypes.any,
  onConfirm: PropTypes.func,
  onDisabled: PropTypes.func,
  dynamicFormData: PropTypes.shape({
    form_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    intr_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    type: PropTypes.string,
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        field_name: PropTypes.string,
        value: PropTypes.any,
        reason: PropTypes.string,
        sequence: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ),
    headers: PropTypes.arrayOf(
      PropTypes.shape({
        position: PropTypes.string,
      }),
    ),
    signatures: PropTypes.arrayOf(
      PropTypes.shape({
        position: PropTypes.string,
      }),
    ),
    footers: PropTypes.arrayOf(
      PropTypes.shape({
        position: PropTypes.string,
      }),
    ),
  }),
  setDynamicFormData: PropTypes.func,
  selectedComponent: PropTypes.shape({
    swm_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    vc_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    vs_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  componentType: PropTypes.string,
};

export default DynamicFormOld;

const styles = {
  intDetailsContainer: {
    px: 2,
    height: "100%",
    border: "1px solid grey",
    borderRadius: 1,
  },
  intDetailsTxt: {
    textAlign: "start",
    alignContent: "center",
  },
  txtBold: {
    fontWeight: "bold",
  },
};
